import { tenantBillingFieldsSchema, DEFAULT_CREDIT_DAYS } from '@unibyte/contracts';
import { useAdminLocale } from '../lib/admin-locale.js';
import { Field, TextInput, Textarea } from './ui/index.js';

export interface TenantBillingValues {
  creditDays: string;
  taxId: string;
  taxOffice: string;
  profession: string;
  street: string;
  streetNumber: string;
  city: string;
  postalCode: string;
  phone: string;
  email: string;
  notes: string;
}

export function emptyTenantBilling(): TenantBillingValues {
  return {
    creditDays: String(DEFAULT_CREDIT_DAYS),
    taxId: '',
    taxOffice: '',
    profession: '',
    street: '',
    streetNumber: '',
    city: '',
    postalCode: '',
    phone: '',
    email: '',
    notes: '',
  };
}

export function tenantBillingFromRow(row: {
  creditDays?: number | null;
  taxId?: string | null;
  taxOffice?: string | null;
  profession?: string | null;
  street?: string | null;
  streetNumber?: string | null;
  city?: string | null;
  postalCode?: string | null;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
}): TenantBillingValues {
  return {
    creditDays: String(row.creditDays ?? DEFAULT_CREDIT_DAYS),
    taxId: row.taxId ?? '',
    taxOffice: row.taxOffice ?? '',
    profession: row.profession ?? '',
    street: row.street ?? '',
    streetNumber: row.streetNumber ?? '',
    city: row.city ?? '',
    postalCode: row.postalCode ?? '',
    phone: row.phone ?? '',
    email: row.email ?? '',
    notes: row.notes ?? '',
  };
}

export function parseTenantBilling(
  values: TenantBillingValues,
): { ok: true; value: Record<string, unknown> } | { ok: false; message: string } {
  const rawDays = values.creditDays.trim();
  if (!/^\d{1,3}$/.test(rawDays)) {
    return { ok: false, message: 'Credit days must be an integer from 0 to 365.' };
  }
  const parsed = tenantBillingFieldsSchema.safeParse({
    creditDays: Number(rawDays),
    taxId: values.taxId,
    taxOffice: values.taxOffice,
    profession: values.profession,
    street: values.street,
    streetNumber: values.streetNumber,
    city: values.city,
    postalCode: values.postalCode,
    phone: values.phone,
    email: values.email,
    notes: values.notes,
  });
  if (!parsed.success) {
    return {
      ok: false,
      message: parsed.error.issues[0]?.message ?? 'Check the billing details.',
    };
  }
  return { ok: true, value: parsed.data };
}

export function TenantBillingFields(props: {
  values: TenantBillingValues;
  onChange: (values: TenantBillingValues) => void;
  disabled?: boolean;
}) {
  const { text } = useAdminLocale();

  function set(key: keyof TenantBillingValues, value: string): void {
    props.onChange({ ...props.values, [key]: value });
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
          {text('Στοιχεία τιμολόγησης', 'Billing details')}
        </p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {text(
            'Προαιρετικά, εκτός από τις ημέρες πίστωσης. Ισχύουν για νέα τιμολόγια.',
            'Optional, except for credit days. Applies to new invoices.',
          )}
        </p>
      </div>
      <Field
        label={text('Ημέρες πίστωσης', 'Credit days')}
        hint={text('Ημέρες από την έκδοση μέχρι τη λήξη.', 'Days from issue to due date.')}
      >
        <TextInput
          required
          inputMode="numeric"
          disabled={props.disabled}
          value={props.values.creditDays}
          onChange={(event) => set('creditDays', event.target.value)}
        />
      </Field>
      <Field label={text('ΑΦΜ', 'Tax ID')} hint={text('9 ψηφία.', '9 digits.')}>
        <TextInput
          inputMode="numeric"
          autoComplete="off"
          disabled={props.disabled}
          value={props.values.taxId}
          onChange={(event) => set('taxId', event.target.value)}
        />
      </Field>
      <Field label={text('Δ.Ο.Υ.', 'Tax office')}>
        <TextInput
          disabled={props.disabled}
          value={props.values.taxOffice}
          onChange={(event) => set('taxOffice', event.target.value)}
        />
      </Field>
      <Field label={text('Επάγγελμα', 'Profession')}>
        <TextInput
          disabled={props.disabled}
          value={props.values.profession}
          onChange={(event) => set('profession', event.target.value)}
        />
      </Field>
      <Field label={text('Οδός', 'Street')}>
        <TextInput
          autoComplete="street-address"
          disabled={props.disabled}
          value={props.values.street}
          onChange={(event) => set('street', event.target.value)}
        />
      </Field>
      <Field label={text('Αριθμός', 'Number')}>
        <TextInput
          disabled={props.disabled}
          value={props.values.streetNumber}
          onChange={(event) => set('streetNumber', event.target.value)}
        />
      </Field>
      <Field label={text('Πόλη', 'City')}>
        <TextInput
          autoComplete="address-level2"
          disabled={props.disabled}
          value={props.values.city}
          onChange={(event) => set('city', event.target.value)}
        />
      </Field>
      <Field label={text('ΤΚ', 'Postal code')} hint={text('5 ψηφία.', '5 digits.')}>
        <TextInput
          inputMode="numeric"
          autoComplete="postal-code"
          disabled={props.disabled}
          value={props.values.postalCode}
          onChange={(event) => set('postalCode', event.target.value)}
        />
      </Field>
      <Field label={text('Τηλέφωνο', 'Phone')}>
        <TextInput
          type="tel"
          autoComplete="tel"
          disabled={props.disabled}
          value={props.values.phone}
          onChange={(event) => set('phone', event.target.value)}
        />
      </Field>
      <Field label="Email">
        <TextInput
          type="email"
          autoComplete="email"
          disabled={props.disabled}
          value={props.values.email}
          onChange={(event) => set('email', event.target.value)}
        />
      </Field>
      <div className="sm:col-span-2">
        <Field label={text('Σημειώσεις', 'Notes')}>
          <Textarea
            rows={3}
            disabled={props.disabled}
            value={props.values.notes}
            onChange={(event) => set('notes', event.target.value)}
          />
        </Field>
      </div>
    </div>
  );
}
